import type { SerializedStyles } from "@emotion/react";
import type { ReactNode } from "react";
import { useCallback } from "react";
import { useDropzone, type FileRejection, type FileError } from "react-dropzone";

import { Attachment, Typography } from "@aviary";
import type { FileWithPreview } from "@shared/types/fileWithPreview";

import * as styles from "./Dropzone.styles";

/**
 * TODO: Handle showing previews of specific file types i.e: images
 */

interface BaseProps {
  file: File | FileWithPreview | null;
  supportedFileTypes: string;
  successMessage?: {
    heading: string;
    description: string;
  };
  onRemoveFile?: () => void;
  onError?: (error?: FileError) => void;
  children: ReactNode;
  previewUrl?: string;
  isAttachmentFullWidth?: boolean;
  customStyles?: SerializedStyles;
  noDrag?: boolean;
}

interface SingleFileProps extends BaseProps {
  multiple?: false;
  onUploadFile: (file: File) => void;
}

interface MultipleFileProps extends BaseProps {
  multiple: true;
  onUploadFile: (files: File[]) => void;
}

type Props = SingleFileProps | MultipleFileProps;

const Dropzone = ({
  file,
  supportedFileTypes,
  successMessage,
  onRemoveFile,
  onUploadFile,
  onError,
  children,
  previewUrl,
  isAttachmentFullWidth,
  customStyles,
  multiple = false,
  noDrag = false,
}: Props) => {
  const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections?.length > 0 && onError) {
      onError(fileRejections[0].errors[0]);
    } else if (multiple) {
      (onUploadFile as (files: File[]) => void)(acceptedFiles);
    } else {
      (onUploadFile as (file: File) => void)(acceptedFiles[0]);
    }
  }, []);

  const dropzoneStyles = () => {
    return [styles.zoneText, isDragActive && styles.dropzoneIsDroppable];
  };

  const { isDragActive, getRootProps, getInputProps } = useDropzone({
    accept: supportedFileTypes,
    onDrop,
    multiple,
    noDrag,
    useFsAccessApi: false,
  });

  if (file) {
    return (
      <div>
        <Attachment
          fileName={file.name}
          handleRemove={onRemoveFile}
          customIcon={!!previewUrl && <div css={styles.fileThumbnail(previewUrl)} />}
          css={styles.fileWrapper}
          data-testid="file-name"
          isFullWidth={isAttachmentFullWidth}
        />
        {!!successMessage && (
          <div css={styles.successBlock}>
            <Typography type="h5" isMarginless css={styles.successText}>
              {successMessage.heading}
            </Typography>
            <Typography>{successMessage.description}</Typography>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="dropzone" css={styles.dropzoneContainer}>
      <div {...getRootProps()} css={[dropzoneStyles(), customStyles ? customStyles : ""]}>
        <input data-testid="dropzone-input" data-e2e="dropzone-input" {...getInputProps()} />
        <div css={styles.readyContainer}>{children}</div>
      </div>
    </div>
  );
};

export { Dropzone };
